'use client';

import React, { memo, useCallback, useEffect, useMemo } from 'react';
import Link from 'next/link';
import equal from 'fast-deep-equal';
import { CheckIcon, Loader2, SquareArrowOutUpRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { ShareIcon } from '@repo/ui/components/icons/share-icon';
import { Button } from '@repo/ui/components/ui/button';
import { formatDateDistanceToNowDynamic } from '@repo/ui/lib/utils';

import { useYtShare } from '@/hooks/use-yt-share';
import { usePostMessage } from '@/providers/post-message-provider';

interface YtVideoCardHeaderProps {
  youtubeId: string;
  uploadDate: string;
  shareUrl?: string | null;
  channelHandle?: string | null;
  channelName: string;
  url: string;
  hideAnchor?: boolean;
}

const PureYtVideoCardHeader: React.FC<YtVideoCardHeaderProps> = ({
  youtubeId,
  uploadDate,
  shareUrl,
  channelHandle,
  channelName,
  url,
  hideAnchor = false,
}) => {
  const { sendRedirectMessage } = usePostMessage();
  const {
    handleShare,
    isSharingUrlPending,
    isSharingUrlSuccess,
    isSharingUrlCopied,
    setIsSharingUrlCopied,
  } = useYtShare();

  const formattedDate = useMemo(() => {
    const publishedAt = uploadDate;

    if (!publishedAt) return null;

    return formatDateDistanceToNowDynamic(publishedAt).replace('about ', '');
  }, [uploadDate]);

  const redirectToVideo = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const shouldOpenInNewTab =
        e.ctrlKey || // Ctrl+click on Windows/Linux
        e.metaKey || // Cmd+click on macOS
        e.shiftKey || // Shift+click (opens in new window/tab)
        e.button === 1; // Middle mouse button click

      const shouldSendTheMessage = e.button === 0 || shouldOpenInNewTab;
      if (shouldSendTheMessage) {
        sendRedirectMessage(url, shouldOpenInNewTab);
      }
    },
    [url, sendRedirectMessage],
  );

  useEffect(() => {
    if (isSharingUrlSuccess) {
      setIsSharingUrlCopied(true);
      const timeout = setTimeout(() => {
        setIsSharingUrlCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isSharingUrlSuccess, setIsSharingUrlCopied]);

  return (
    <div className="flex h-3.5 w-full px-2 cred-md:px-[9.33px] items-center gap-1.5 text-[#757575] cred-md:h-4 cred-md:gap-[7px] cred-lg:h-[18px] cred-lg:gap-[7.6px] cred-xl:h-5 cred-xl:gap-2.5 3xl:h-6">
      <Link
        href={`/youtube/${channelHandle}`}
        onMouseDown={(e) => e.stopPropagation()}
        className="max-w-full shrink min-w-0"
      >
        <span className="whitespace-nowrap line-clamp-1 text-ellipsis text-black text-xs font-bold leading-[15px] cred-md:text-[13px] cred-md:leading-[18px] cred-lg:text-sm cred-lg:leading-4 cred-xl:text-[15px] cred-xl:leading-[18px]">
          {channelName || ''}
        </span>
      </Link>
      <span className="shrink-0 text-lg leading-none">•</span>
      <span className="shrink-0 text-[10px] leading-[15px] cred-md:text-xs cred-md:leading-[18px] cred-lg:text-[13px] cred-lg:leading-[19px] cred-xl:text-sm cred-xl:leading-4">
        {formattedDate}
      </span>
      <div className="ml-auto shrink-0 flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          disabled={isSharingUrlPending}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleShare({ ytId: youtubeId, shareUrl });
          }}
          className="size-5 stroke-[1.7px] text-black md:size-6 [&_svg]:size-[19px] md:[&_svg]:size-[18px] 3xl:[&_svg]:size-5 md:[&_svg]:stroke-2"
        >
          <AnimatePresence mode="wait">
            {isSharingUrlPending ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                }}
                transition={{ duration: 0.05, ease: 'easeInOut' }}
              >
                <Loader2 className="animate-spin" />
              </motion.div>
            ) : isSharingUrlCopied ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: { duration: 0.08, ease: 'easeInOut' },
                }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <CheckIcon />
              </motion.div>
            ) : (
              <motion.div
                key="share"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: { duration: 0.08, ease: 'easeInOut' },
                }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <ShareIcon />
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
        {!hideAnchor && (
          <Button
            asChild
            size="icon"
            variant="ghost"
            className="size-5 text-black md:size-6 [&_svg]:size-[19px] md:[&_svg]:size-[18px] 3xl:[&_svg]:size-5 md:[&_svg]:stroke-[2.3px]"
          >
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onMouseDown={redirectToVideo}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <SquareArrowOutUpRight />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

export const YtVideoCardHeader = memo(
  PureYtVideoCardHeader,
  (prevProps, nextProps) => {
    if (prevProps.hideAnchor !== nextProps.hideAnchor) {
      return false;
    }
    if (!equal(prevProps.youtubeId, nextProps.youtubeId)) {
      return false;
    }
    if (prevProps.shareUrl !== nextProps.shareUrl) {
      return false;
    }
    if (!equal(prevProps.uploadDate, nextProps.uploadDate)) {
      return false;
    }
    if (!equal(prevProps.channelHandle, nextProps.channelHandle)) {
      return false;
    }
    if (!equal(prevProps.channelName, nextProps.channelName)) {
      return false;
    }
    if (!equal(prevProps.url, nextProps.url)) {
      return false;
    }
    return true;
  },
);
